/**
 * An API error that keeps what the server said, not only its message.
 *
 * `api.ts` used to throw a plain `Error(message)`, so a caller could not tell
 * one refusal from another. Some refusals need more than a toast — a session
 * that ends after the learner's subscription is shown in a dialog with both
 * dates, and an admin action outside it asks the admin to confirm — so the
 * status, the server's `error_code` and the body travel with the error.
 * It is still an `Error`, so every existing `error.message` keeps working.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    const code = (data as { error_code?: unknown } | null)?.error_code;
    this.code = typeof code === 'string' ? code : null;
  }
}

/** The server's `subscription` context on a `subscription_window_exceeded` refusal. */
export interface SubscriptionWindowContext {
  reason: 'session_outside_subscription_window';
  expires_at: string | null;
  session_start_at: string | null;
  session_end_at: string | null;
  shortfall_days: number | null;
  /** True for admin actions that may go ahead once the admin confirms. */
  requires_confirmation: boolean;
}

export const SUBSCRIPTION_WINDOW_ERROR = 'subscription_window_exceeded';

/** The refusal context when `error` is a session-outside-subscription refusal, else null. */
export const subscriptionWindowError = (error: unknown): SubscriptionWindowContext | null => {
  if (!(error instanceof ApiError) || error.code !== SUBSCRIPTION_WINDOW_ERROR) return null;
  const subscription = (error.data as { subscription?: SubscriptionWindowContext } | null)?.subscription;
  return subscription ?? null;
};
